import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import type { GridRowSelectionModel } from '@mui/x-data-grid-premium'

import SendNotificationDialog from '@/components/notification-send/SendNotificationDialog'
import { useAppTheme } from '@/theme/ThemeContext'

import Send from '@assets/SendIcon.svg'

import type { AlertsListRow } from './alertsListFilters'
import {
  MAX_SEND_ALERTS,
  needsMatchingRows,
  premiseIdsOfSelection,
} from './sendAudience'

export type MatchingRows = { rows: AlertsListRow[]; failed: boolean }

type Props = {
  siteId: string | undefined
  /** The rows the list shows now. */
  rows: AlertsListRow[]
  /** Every alert the filters match, across pages. */
  total: number
  selection: GridRowSelectionModel
  /** Reads the first `size` rows the filters match. */
  loadMatchingRows: (size: number) => Promise<MatchingRows>
}

type Matching =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; rows: AlertsListRow[] }

const TOO_LARGE = `הבחירה גדולה מדי לשליחה (יותר מ-${MAX_SEND_ALERTS} התראות). צמצמו את הסינון או סמנו שורות בעמוד.`
const MATCHING_FAILED = 'רשימת הנמענים לא נטענה. נסו שוב.'
const NO_CUSTOMER = 'לשורות שסומנו אין צרכן לשליחה.'

const AdditionalActionsSendBar: React.FC<Props> = ({
  siteId,
  rows,
  total,
  selection,
  loadMatchingRows,
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const accountId = Number(siteId)
  const [dialogPremiseIds, setDialogPremiseIds] = useState<number[] | null>(
    null
  )

  const readsMatching = needsMatchingRows(selection, rows, total)
  const tooLarge = readsMatching && total > MAX_SEND_ALERTS
  const [matching, setMatching] = useState<Matching>({ status: 'idle' })
  useEffect(() => {
    if (!readsMatching || tooLarge) {
      setMatching({ status: 'idle' })
      return
    }
    let active = true
    setMatching({ status: 'loading' })
    loadMatchingRows(total)
      .then(result => {
        if (!active) return
        setMatching(
          result.failed
            ? { status: 'failed' }
            : { status: 'ready', rows: result.rows }
        )
      })
      .catch(() => {
        if (active) setMatching({ status: 'failed' })
      })
    return () => {
      active = false
    }
  }, [readsMatching, tooLarge, total, loadMatchingRows])

  const recipientRows = readsMatching
    ? matching.status === 'ready'
      ? matching.rows
      : null
    : rows
  const premiseIds = useMemo(
    () =>
      recipientRows ? premiseIdsOfSelection(recipientRows, selection) : [],
    [recipientRows, selection]
  )
  const anythingTicked =
    selection.type === 'exclude'
      ? total > selection.ids.size
      : selection.ids.size > 0

  // The visible note covers the states that a tooltip would hide from touch users.
  let note: string | null = null
  let reason: string | null = null
  if (!anythingTicked) reason = 'סמנו התראה אחת לפחות'
  else if (tooLarge) note = TOO_LARGE
  else if (readsMatching && matching.status === 'failed') note = MATCHING_FAILED
  else if (readsMatching && matching.status !== 'ready')
    reason = 'טוען את רשימת הנמענים'
  else if (premiseIds.length === 0) note = NO_CUSTOMER
  const blocked = note ?? reason
  const countKnown = recipientRows !== null
  const label = anythingTicked ? (countKnown ? premiseIds.length : '…') : 0

  const textColor = isDarkMode ? '#BBD7FF' : '#003F61'
  const textSx = {
    color: textColor,
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'right' as const,
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          mt: 1.5,
        }}
      >
        <Box
          sx={{
            width: 'fit-content',
            px: 2,
            py: 0.8,
            border: `1px solid ${isDarkMode ? '#43597F' : '#E1E8EE'}`,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: 3,
          }}
        >
          <Tooltip title={blocked ?? ''}>
            <span>
              <Button
                variant="contained"
                disabled={blocked !== null}
                onClick={() => setDialogPremiseIds(premiseIds)}
                endIcon={
                  <img
                    src={Send}
                    alt="send"
                    style={{ filter: 'brightness(0) invert(1)', height: 18 }}
                  />
                }
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  bgcolor: '#476C9B',
                  color: '#fff',
                  px: 2.5,
                  py: 0.8,
                  fontWeight: 600,
                  fontSize: '14px',
                  fontFamily: 'Rubik, sans-serif',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&.Mui-disabled': {
                    bgcolor: isDarkMode ? '#476C9B' : '#9AA7B5',
                    color: '#fff',
                    opacity: isDarkMode ? 0.6 : 1,
                  },
                }}
              >
                שלח ({label})
              </Button>
            </span>
          </Tooltip>
        </Box>

        {note ? (
          <Typography
            dir="rtl"
            sx={{ ...textSx, fontSize: '13px', opacity: 0.85 }}
          >
            {note}
          </Typography>
        ) : null}
      </Box>

      <SendNotificationDialog
        open={dialogPremiseIds !== null}
        onClose={() => setDialogPremiseIds(null)}
        accountId={accountId}
        audience={{ kind: 'premises', ids: dialogPremiseIds ?? [] }}
      />
    </>
  )
}

export default AdditionalActionsSendBar
